// R-131-FIN-DECISION-A — shared preset factory helpers.

import { effectivePower, bucketMidpointMw } from '../../projectIntent';
import { HOURS_PER_YEAR, KW_PER_MW } from '../../projectFinance';
import { resolveIntentPowerStack, resolveIntentPowerStackBand } from '../../powerStack';
import { resolveGenerationNameplateForTech } from '../../phaseFeasibility';
import type { FuelCellPhaseConfig, ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type {
  Pathway,
  PathwayCapex,
  PathwayPhase,
  PathwayPhaseKind,
  PathwayRevenue,
  PhaseSourceMixComponent,
  ResolvedFuelCellEconomicSnapshot,
  TechnologyId,
} from '../types';
import { withSyncedRiskPremium } from '../riskPremium';
import { GAS_CC_BENCHMARKS } from './gasCombinedCycle';
import { GAS_SC_BENCHMARKS } from './gasSimpleCycle';
import { NUCLEAR_SMR_BENCHMARKS } from './nuclearSmr';
import { NUCLEAR_LARGE_BENCHMARKS } from './nuclearLarge';
import { SOLAR_BESS_BENCHMARKS } from './solarBess';
import { WIND_BESS_BENCHMARKS } from './windBess';
import { GRID_TARIFF_BENCHMARKS } from './gridTariff';
import { DEFAULT_PACKAGE_BY_TECHNOLOGY_ID, computeFuelCellNetAcHeatRateHhv } from './stationaryFuelCell';
import { lookupTransmissionTariff } from '../grid/transmissionTariff';

/** Default analysis WACC when intent carries no financing assumption. */
export const DEFAULT_BASE_WACC_PCT = 7.5;

// ───────────────────────────────────────────────────────────────────────────
// VALUE-OF-SERVED-LOAD — single source of truth for the comparison NPV credit.
//
// WHY THIS EXISTS: pathway-compare NPV@WACC must be VALUE-CONSISTENT. NPV =
// (value of served load) − (own cost). To make the candidate NPVs apples-to-
// apples — so the NPV ranking tracks the LCOE ranking (lower cost ⇒ higher /
// less-negative NPV) — EVERY pathway, INCLUDING the grid-tariff baseline, must
// be credited the SAME value per MWh served. Previously grid-tariff carried
// `ppaPricePerMwhUsd = 0` (a pure-cost stream) while every other preset carried
// 65, so grid's NPV looked ~4x worse purely from missing revenue even though its
// LCOE was only ~1.45x higher. Centralising the credit here removes that
// apples-to-oranges defect and prevents presets from drifting apart again.
//
// CHOSEN VALUE-OF-SERVED-LOAD = 65 USD/MWh (the long-standing
// DEFAULT_PPA_PRICE). SOURCE / ASSUMPTION: this is the existing load-serving
// PPA-price default the model already used for every NON-grid pathway. The
// PREFERRED basis — the avoided-cost benchmark (the grid retail / delivered
// tariff the datacenter would otherwise pay, datasets.electricity.retailRate /
// the grid-tariff pathway's effective delivered cost) — is NOT cleanly
// available at preset-construction time: presets receive only `intent` +
// `siteContext`, and the retail rate lives in the site-evidence / SED layer that
// is not threaded into preset construction. Per the value-consistent fallback,
// we therefore apply this single existing default UNIFORMLY to ALL pathways
// (grid included). It is a flagged ASSUMPTION, not a sited/ISO-specific rate;
// when an avoided-cost rate is later threaded through, override this ONE
// constant (and/or pass a per-comparison value via the revenue factory) and all
// pathways move together.
//
// NOTE (escalator): the value-of-served-load escalates at a single uniform rate
// (below) for every pathway too, so the value STREAM — not just the year-1 price
// — is identical per MWh served across pathways.
//
// HORIZON CAVEAT: pathways have different design lives (grid 30y, SMR 40y). A
// value-consistent NPV over different lives is still partly horizon-skewed (a
// longer-lived asset accrues more discounted value-minus-cost years). This fix
// makes the credit consistent but does NOT equalise horizons; see the report /
// `pathwayCompareMetrics` for the residual-skew note. No perpetuity / terminal
// value is added.

/** Canonical value-of-served-load credited to EVERY pathway (USD/MWh). */
export const VALUE_OF_SERVED_LOAD_USD_PER_MWH = 65;

/** Uniform escalation of the value-of-served-load credit (percent per year). */
export const VALUE_OF_SERVED_LOAD_ESCALATOR_PCT_PER_YEAR = 2;

/**
 * Default PPA price for load-serving behind-the-meter / on-site generation.
 *
 * @deprecated Retained as a backward-compatible alias of
 * {@link VALUE_OF_SERVED_LOAD_USD_PER_MWH}. New code should build the revenue
 * block via {@link valueOfServedLoadRevenue} so the value-of-served-load credit
 * stays identical across ALL pathways (grid included) and cannot drift.
 */
export const DEFAULT_PPA_PRICE_USD_PER_MWH = VALUE_OF_SERVED_LOAD_USD_PER_MWH;

/**
 * Build the VALUE-CONSISTENT revenue block credited to a pathway.
 *
 * Centralises the value-of-served-load so the pathway-compare NPV is
 * apples-to-apples: the credited price ({@link VALUE_OF_SERVED_LOAD_USD_PER_MWH})
 * and its escalator ({@link VALUE_OF_SERVED_LOAD_ESCALATOR_PCT_PER_YEAR}) are
 * IDENTICAL for every pathway, including the grid-tariff baseline. The revenue a
 * pathway actually earns still varies — because each pathway's served energy
 * (`annualMwh`, which folds in capacity factor / availability) differs — but the
 * PRICE per served MWh is the same, which is exactly what makes the NPVs
 * comparable (and the NPV ranking track the LCOE ranking).
 *
 * @param termYears Years over which the load is served (the pathway design
 *   life). The credit is paid for the same years the load is served.
 * @param availabilityPct Pathway availability (carried through verbatim; the
 *   energy basis already reflects it — see annualRevenueUsd).
 */
export function valueOfServedLoadRevenue(args: {
  termYears: number;
  availabilityPct: number;
}): PathwayRevenue {
  return {
    ppaPricePerMwhUsd: VALUE_OF_SERVED_LOAD_USD_PER_MWH,
    // C15 — no per-pathway override. The escalator parameter existed only so
    // solar and wind could escalate the served-load credit at 1.5%/yr; that is
    // a property of a technology PPA, not of the load being served, and it made
    // the "uniform" value basis untrue for two of the seven pathways. Removed
    // rather than left unused, so the next preset cannot reintroduce the split.
    ppaEscalatorPctPerYear: VALUE_OF_SERVED_LOAD_ESCALATOR_PCT_PER_YEAR,
    ppaTermYears: args.termYears,
    availabilityPct: args.availabilityPct,
  };
}

/** Fallback capacity when intent power requirement is undecided. */
export const DEFAULT_CAPACITY_MW = 100;

/** Standard 4-hour BESS duration for solar/wind + storage presets. */
export const DEFAULT_BESS_DURATION_HOURS = 4;

/** Placeholder grid interconnection queue (R-131-FIN-DECISION-D). */
export const PLACEHOLDER_QUEUE_YEARS = {
  medianYears: 3.5,
  p10Years: 2,
  p90Years: 6,
} as const;

export interface PathwayPresetOptions {
  /** Deep partial merge applied after defaults are computed. */
  overrides?: Partial<Pathway>;
  /** Explicit per-phase fuel-cell configuration snapshot when building a fuel-cell pathway. */
  fuelCellConfig?: FuelCellPhaseConfig;
  /** Originating phase index for multi-phase disambiguation. */
  phaseIndex?: number;
}

export type PresetForFn = (
  intent: ProjectIntent,
  siteContext: SiteContext,
  options?: PathwayPresetOptions,
) => Pathway;

/**
 * Resolve the datacenter LOAD in MW from project intent.
 *
 * This is the FACILITY/grid DEMAND (the load to be served) — NOT a generation
 * nameplate. Generation presets must gross this up to a nameplate that can
 * actually serve the load (see {@link resolveNameplateMw}); the raw load is the
 * basis for load-tracking costs such as the grid demand charge.
 *
 * Resolution order: exact `powerRequirementMW` → bucket midpoint. (The
 * power-basis / PUE resolution lives in {@link resolveFacilityLoadMw}, which
 * layers `resolveIntentPowerStack` on top of this raw figure.)
 */
export function resolveCapacityMw(intent: ProjectIntent): number {
  const power = effectivePower(intent);
  if (power.mw != null && power.mw > 0) return power.mw;
  return bucketMidpointMw(power.bucket) ?? DEFAULT_CAPACITY_MW;
}

/**
 * Resolve the FACILITY LOAD (MW) the generation must serve, basis-aware.
 *
 * Routes the entered figure through the power stack (basis / PUE), which is the
 * single source of truth for "what is the facility/grid demand". When the stack
 * cannot resolve a facility figure (undecided / no phases), falls back to the
 * raw {@link resolveCapacityMw} bucket so a load is always available.
 *
 * This is the LOAD — demand charges ride this. The generation NAMEPLATE is a
 * grossed-up multiple of it (see {@link resolveNameplateMw}).
 */
export function resolveFacilityLoadMw(intent: ProjectIntent): number {
  const facilityMW = resolveIntentPowerStack(intent).facilityMW;
  if (facilityMW != null && facilityMW > 0) return facilityMW;

  // BUCKET FALLBACK — basis-aware.
  //
  // This used to drop straight to `resolveCapacityMw`, which returns the RAW
  // bucket midpoint with no basis or PUE applied. On an `it-load` basis that
  // silently fed finance an IT figure where a FACILITY figure belongs: a
  // "200–500 MW" bucket became a 350 MW facility load when the basis-correct
  // figure is 350 × PUE (≈490 MW at PUE 1.4) — a ~40% understatement of the
  // load every demand charge and nameplate gross-up rides on.
  //
  // The band's `facilityMW.sizingBasis` is that same midpoint pushed through the
  // power stack, so the basis and PUE are applied exactly as they are for an
  // exact figure. On the DEFAULT facility basis the two are identical, so this
  // changes nothing for facility-basis sites — it only repairs it-load ones.
  const bandFacilityMW = resolveIntentPowerStackBand(intent)?.facilityMW.sizingBasis;
  if (bandFacilityMW != null && Number.isFinite(bandFacilityMW) && bandFacilityMW > 0) {
    return bandFacilityMW;
  }

  // Undecided / unresolvable — the documented last-resort default.
  return resolveCapacityMw(intent);
}

/**
 * Resolve the generation NAMEPLATE (MW) for a single technology serving the
 * intent's facility load.
 *
 * The facility LOAD ({@link resolveFacilityLoadMw}) is grossed up to the
 * nameplate that can SERVE a firm 24/7 load via
 * {@link resolveGenerationNameplateForTech} (the ONE source of truth for
 * load→nameplate sizing — energy + firm-capacity tiers, planning reserve):
 *   - grid-tariff  ≈ load × 1.18 (firm-only, ~97% capacity credit + 15% reserve)
 *   - gas-cc/gas-sc ≈ load × 1.25 (firm dispatchable, ~92% credit + reserve)
 *   - nuclear      ≈ load × 1.24 (≈93% credit + reserve)
 *   - solar/wind   ≈ load × 3.3–3.8 (low ELCC on a flat 24/7 load → real overbuild)
 *
 * `'mixed'` has no single point-source sizing → returns the bare load (the mix
 * is sized per-component upstream; this fallback never undersizes silently).
 * A non-finite / ≤0 nameplate (e.g. zero load) falls back to the load itself.
 */
export function resolveNameplateMw(intent: ProjectIntent, tech: TechnologyId): number {
  const load = resolveFacilityLoadMw(intent);
  const nameplate = resolveGenerationNameplateForTech(load, tech);
  return Number.isFinite(nameplate) && nameplate > 0 ? nameplate : load;
}

/** ISO date for pathway phase anchors — intent in-service date or +36 months default. */
export function resolveProjectStartDate(intent: ProjectIntent): string {
  if (intent.inServiceDate) return intent.inServiceDate;
  const d = new Date();
  d.setFullYear(d.getFullYear() + 3);
  d.setMonth(0, 1);
  return d.toISOString().slice(0, 10);
}

export function addMonthsIso(isoDate: string, months: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function capacityKw(capacityMw: number): number {
  return capacityMw * KW_PER_MW;
}

export function annualMwhFromCf(capacityMw: number, capacityFactorPct: number): number {
  return capacityMw * HOURS_PER_YEAR * (capacityFactorPct / 100);
}

export function capexFromPerKw(capacityMw: number, capexPerKwUsd: number): number {
  return capacityKw(capacityMw) * capexPerKwUsd;
}

/** Even spread of CAPEX across construction months mapped to calendar years. */
export function buildUniformCapexOutflows(
  startDate: string,
  constructionMonths: number,
  totalCapexUsd: number,
): PathwayCapex['outflowsByYear'] {
  if (constructionMonths <= 0 || totalCapexUsd <= 0) {
    return totalCapexUsd > 0 ? [{ year: new Date(startDate).getUTCFullYear(), usd: totalCapexUsd }] : [];
  }
  const startYear = new Date(`${startDate}T00:00:00Z`).getUTCFullYear();
  const monthly = totalCapexUsd / constructionMonths;
  const byYear = new Map<number, number>();
  for (let m = 0; m < constructionMonths; m += 1) {
    const phaseDate = addMonthsIso(startDate, m);
    const year = new Date(`${phaseDate}T00:00:00Z`).getUTCFullYear();
    byYear.set(year, (byYear.get(year) ?? 0) + monthly);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => a - b)
    .map(([year, usd]) => ({ year, usd }));
}

export interface StandardBtmPhaseInput {
  startDate: string;
  constructionMonths: number;
  designLifeYears: number;
  totalCapexUsd: number;
  decommissioningUsd: number;
  annualMwh: number;
  annualOpexUsd: number;
  decommissioningMonths?: number;
}

/** Construction → operation → decommissioning lifecycle for BTM presets. */
export function buildStandardBtmPhases(input: StandardBtmPhaseInput): PathwayPhase[] {
  const {
    startDate,
    constructionMonths,
    designLifeYears,
    totalCapexUsd,
    decommissioningUsd,
    annualMwh,
    annualOpexUsd,
    decommissioningMonths = 12,
  } = input;

  const operationStart = addMonthsIso(startDate, constructionMonths);
  const operationMonths = designLifeYears * 12;
  const decommStart = addMonthsIso(operationStart, operationMonths);

  const phases: PathwayPhase[] = [
    {
      kind: 'construction',
      startDate,
      durationMonths: constructionMonths,
      capexUsd: totalCapexUsd,
    },
    {
      kind: 'operation',
      startDate: operationStart,
      durationMonths: operationMonths,
      annualMwh,
      annualOpexUsd,
    },
  ];

  if (decommissioningUsd > 0) {
    phases.push({
      kind: 'decommissioning',
      startDate: decommStart,
      durationMonths: decommissioningMonths,
      capexUsd: decommissioningUsd,
    });
  }

  return phases;
}

export function buildCapexBlock(
  startDate: string,
  constructionMonths: number,
  baseUsd: number,
  extras: Pick<PathwayCapex, 'contingencyPct' | 'substationUsd' | 'transmissionUpgradeUsd' | 'fuelLogisticsUsd'> = {
    contingencyPct: 0,
  },
): PathwayCapex {
  const contingencyUsd = baseUsd * (extras.contingencyPct / 100);
  const totalUsd =
    baseUsd +
    contingencyUsd +
    (extras.substationUsd ?? 0) +
    (extras.transmissionUpgradeUsd ?? 0) +
    (extras.fuelLogisticsUsd ?? 0);

  return {
    baseUsd,
    contingencyPct: extras.contingencyPct,
    substationUsd: extras.substationUsd,
    transmissionUpgradeUsd: extras.transmissionUpgradeUsd,
    fuelLogisticsUsd: extras.fuelLogisticsUsd,
    outflowsByYear: buildUniformCapexOutflows(startDate, constructionMonths, totalUsd),
  };
}

export function computeAnnualOpexUsd(
  capacityMw: number,
  fixedOmPerKwYearUsd: number,
  variableOmPerMwhUsd: number,
  fuelCostPerMwhUsd: number,
  annualMwh: number,
): number {
  return (
    capacityKw(capacityMw) * fixedOmPerKwYearUsd +
    variableOmPerMwhUsd * annualMwh +
    fuelCostPerMwhUsd * annualMwh
  );
}

/** Deep-merge pathway overrides — nested objects merge one level deep. */
export function mergePathway(base: Pathway, overrides?: Partial<Pathway>): Pathway {
  if (!overrides) return withSyncedRiskPremium(base);
  return withSyncedRiskPremium({
    ...base,
    ...overrides,
    phases: overrides.phases ?? base.phases,
    capex: overrides.capex ? { ...base.capex, ...overrides.capex } : base.capex,
    opex: overrides.opex ? { ...base.opex, ...overrides.opex } : base.opex,
    revenue: overrides.revenue ? { ...base.revenue, ...overrides.revenue } : base.revenue,
    risk: overrides.risk ? { ...base.risk, ...overrides.risk } : base.risk,
    carbon: overrides.carbon ? { ...base.carbon, ...overrides.carbon } : base.carbon,
    lifetime: overrides.lifetime ? { ...base.lifetime, ...overrides.lifetime } : base.lifetime,
  });
}

export function pathwayIdFromTechnology(technology: Pathway['technology'], suffix?: string): string {
  const base = technology.replace(/-/g, '_');
  return suffix ? `${base}_${suffix}` : base;
}

/** Sum operation-phase months — must equal designLifeYears × 12 for sanity checks. */
export function sumOperationPhaseMonths(phases: PathwayPhase[]): number {
  return phases
    .filter((p): p is PathwayPhase & { kind: 'operation' } => p.kind === 'operation')
    .reduce((sum, p) => sum + p.durationMonths, 0);
}

export function sumPhaseMonthsByKind(phases: PathwayPhase[], kind: PathwayPhaseKind): number {
  return phases.filter((p) => p.kind === kind).reduce((sum, p) => sum + p.durationMonths, 0);
}

export interface PathwaySanityResult {
  ok: boolean;
  errors: string[];
}

/** Baseline conservation check for preset defaults. */
export function validatePathwayDefaults(pathway: Pathway): PathwaySanityResult {
  const errors: string[] = [];

  if (pathway.capex.baseUsd <= 0) {
    errors.push('capex.baseUsd must be > 0');
  }
  if (pathway.lifetime.designLifeYears <= 0) {
    errors.push('lifetime.designLifeYears must be > 0');
  }

  const operationMonths = sumOperationPhaseMonths(pathway.phases);
  const expectedOperationMonths = pathway.lifetime.designLifeYears * 12;

  // Grid and hybrid pathways may use multiple operation segments; require total >= design life.
  if (pathway.classification === 'btm') {
    if (operationMonths !== expectedOperationMonths) {
      errors.push(
        `operation phases (${operationMonths} mo) must equal designLifeYears × 12 (${expectedOperationMonths} mo)`,
      );
    }
  } else if (operationMonths < expectedOperationMonths) {
    errors.push(
      `operation phases (${operationMonths} mo) must cover at least designLifeYears × 12 (${expectedOperationMonths} mo)`,
    );
  }

  const constructionMonths = sumPhaseMonthsByKind(pathway.phases, 'construction');
  const decommMonths = sumPhaseMonthsByKind(pathway.phases, 'decommissioning');
  const totalLifecycleMonths = operationMonths + constructionMonths + decommMonths;
  if (totalLifecycleMonths <= 0) {
    errors.push('phases must span a positive lifecycle duration');
  }

  return { ok: errors.length === 0, errors };
}

/** JSON round-trip identity check for Pathway objects. */
export function pathwayJsonRoundTrip(pathway: Pathway): Pathway {
  return JSON.parse(JSON.stringify(pathway)) as Pathway;
}

// ───────────────────────────────────────────────────────────────────────────
// R-131-FIN-DECISION-MIX — per-phase power-source MIX economics.
//
// ADDITIVE: nothing here runs unless a PathwayPhase carries `sourceMix`.
// Every per-source number below is *derived from the existing, sourced
// `*_BENCHMARKS` constants* (NREL ATB / EIA AEO / Lazard LCOE) — no new
// financial values are invented. Solar/Wind effective $/kW folds the BESS
// term at the standard 4-hour duration so it reduces to the same single
// $/kW the standalone presets already use (PV/turbine $/kW + BESS
// $/kWh × 4 h). All flagged modeling assumptions are surfaced by callers
// in `flagsForUser`.
// ───────────────────────────────────────────────────────────────────────────

/** Sourced per-source economics for one MIX component (all values derived). */
export interface SourceEconomics {
  /** Overnight capex, USD per kW of that source's capacity. */
  capexPerKwUsd: number;
  /** Fixed O&M, USD per kW-year. */
  fixedOmPerKwYearUsd: number;
  /** Variable O&M, USD per MWh. */
  variableOmPerMwhUsd: number;
  /** Fuel cost, USD per MWh (0 for renewables / nuclear-fuel-light). */
  fuelCostPerMwhUsd: number;
  /** Net capacity factor, percent (drives annual MWh). */
  capacityFactorPct: number;
  /** Operating emissions, tCO2e per MWh. */
  tCo2ePerMwh: number;
}

/**
 * Resolve sourced per-kW / per-MWh economics for a technology, drawn from
 * its existing `*_BENCHMARKS` block. Built lazily inside a function to keep
 * the preset ↔ shared import cycle safe (benchmarks are only read at call
 * time, never at module-evaluation time).
 *
 * `grid-tariff` is supported as a MIX component because intent postures layer a
 * grid baseline into mixed / grid-primary / gas-bridge pathways (a real,
 * intended scenario — e.g. a grid-primary or gas-bridge site). It is priced as
 * grid IMPORT, not generation (see the `grid-tariff` case), drawing every number
 * from {@link GRID_TARIFF_BENCHMARKS} so the blended grid component matches the
 * standalone grid pathway's LCOE basis exactly.
 *
 * `mixed` remains a META-pathway label (a phase that itself carries a sourceMix),
 * not a leaf source, so it is intentionally unsupported as a MIX component;
 * callers get a thrown error rather than a fabricated number.
 */
export function sourceEconomicsFor(
  powerSource: TechnologyId,
  fuelCellSnapshot?: ResolvedFuelCellEconomicSnapshot,
): SourceEconomics {
  switch (powerSource) {
    case 'gas-cc':
      return {
        capexPerKwUsd: GAS_CC_BENCHMARKS.capexPerKwUsd,
        fixedOmPerKwYearUsd: GAS_CC_BENCHMARKS.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: GAS_SC_BENCHMARKS ? GAS_CC_BENCHMARKS.variableOmPerMwhUsd : 0,
        fuelCostPerMwhUsd: GAS_CC_BENCHMARKS.fuelCostPerMwhUsd,
        capacityFactorPct: GAS_CC_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: GAS_CC_BENCHMARKS.tCo2ePerMwh,
      };
    case 'gas-sc':
      return {
        capexPerKwUsd: GAS_SC_BENCHMARKS.capexPerKwUsd,
        fixedOmPerKwYearUsd: GAS_SC_BENCHMARKS.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: GAS_SC_BENCHMARKS.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: GAS_SC_BENCHMARKS.fuelCostPerMwhUsd,
        capacityFactorPct: GAS_SC_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: GAS_SC_BENCHMARKS.tCo2ePerMwh,
      };
    case 'fuel-cell-sofc':
    case 'fuel-cell-pafc':
    case 'fuel-cell-pem':
    case 'fuel-cell-mcfc': {
      if (fuelCellSnapshot) {
        if (
          fuelCellSnapshot.economicQualificationPosture === 'unsupported-incomplete' ||
          fuelCellSnapshot.capexPerKwUsd == null ||
          fuelCellSnapshot.fixedOmPerKwYearUsd == null ||
          fuelCellSnapshot.variableOmPerMwhUsd == null ||
          fuelCellSnapshot.fuelCostPerMwhUsd == null
        ) {
          throw new RangeError(
            `sourceEconomicsFor: '${powerSource}' carries an unsupported-incomplete fuelCellSnapshot and cannot be priced.`,
          );
        }
        return {
          capexPerKwUsd: fuelCellSnapshot.capexPerKwUsd,
          fixedOmPerKwYearUsd: fuelCellSnapshot.fixedOmPerKwYearUsd,
          variableOmPerMwhUsd: fuelCellSnapshot.variableOmPerMwhUsd,
          fuelCostPerMwhUsd: fuelCellSnapshot.fuelCostPerMwhUsd,
          capacityFactorPct: fuelCellSnapshot.capacityFactorPct,
          tCo2ePerMwh: fuelCellSnapshot.tCo2ePerMwh,
        };
      }
      const pkg = DEFAULT_PACKAGE_BY_TECHNOLOGY_ID[powerSource];
      const heatRate = computeFuelCellNetAcHeatRateHhv({
        efficiencyFractionLhv: pkg.electricalEfficiencyFractionLhv,
        primaryFuel: pkg.primaryFuel,
        suppliedHeatRateHhvBtuPerKwh: pkg.suppliedHeatRateHhvBtuPerKwh,
      });
      const defaultFuelCostPerMwhUsd =
        pkg.benchmarks.assumedBenchmarkDeliveredFuelUsdPerMmbtu * heatRate.heatRateHhvMmbtuPerMwh;
      return {
        capexPerKwUsd: pkg.benchmarks.capexPerKwUsd,
        fixedOmPerKwYearUsd:
          pkg.benchmarks.fixedOmPerKwYearUsd + pkg.benchmarks.ltsaPerKwYearUsd,
        variableOmPerMwhUsd: pkg.benchmarks.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: defaultFuelCostPerMwhUsd,
        capacityFactorPct: pkg.benchmarks.capacityFactorPct,
        tCo2ePerMwh: pkg.benchmarks.tCo2ePerMwh,
      };
    }
    case 'nuclear-smr':
      return {
        capexPerKwUsd: NUCLEAR_SMR_BENCHMARKS.capexPerKwUsd,
        fixedOmPerKwYearUsd: NUCLEAR_SMR_BENCHMARKS.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: NUCLEAR_SMR_BENCHMARKS.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: NUCLEAR_SMR_BENCHMARKS.fuelCostPerMwhUsd,
        capacityFactorPct: NUCLEAR_SMR_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: 0,
      };
    case 'nuclear-large':
      return {
        capexPerKwUsd: NUCLEAR_LARGE_BENCHMARKS.capexPerKwUsd,
        fixedOmPerKwYearUsd: NUCLEAR_LARGE_BENCHMARKS.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: NUCLEAR_LARGE_BENCHMARKS.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: NUCLEAR_LARGE_BENCHMARKS.fuelCostPerMwhUsd,
        capacityFactorPct: NUCLEAR_LARGE_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: 0,
      };
    case 'solar-bess':
      return {
        // PV $/kW + BESS $/kWh × standard 4-h duration → effective $/kW.
        capexPerKwUsd:
          SOLAR_BESS_BENCHMARKS.pvCapexPerKwUsd +
          SOLAR_BESS_BENCHMARKS.bessCapexPerKwhUsd * DEFAULT_BESS_DURATION_HOURS,
        fixedOmPerKwYearUsd: SOLAR_BESS_BENCHMARKS.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: SOLAR_BESS_BENCHMARKS.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: 0,
        capacityFactorPct: SOLAR_BESS_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: 0,
      };
    case 'wind-bess':
      return {
        // Turbine $/kW + BESS $/kWh × standard 4-h duration → effective $/kW.
        capexPerKwUsd:
          WIND_BESS_BENCHMARKS.turbineCapexPerKwUsd +
          WIND_BESS_BENCHMARKS.bessCapexPerKwhUsd * DEFAULT_BESS_DURATION_HOURS,
        fixedOmPerKwYearUsd: WIND_BESS_BENCHMARKS.fixedOmPerKwYearUsd,
        variableOmPerMwhUsd: WIND_BESS_BENCHMARKS.variableOmPerMwhUsd,
        fuelCostPerMwhUsd: 0,
        capacityFactorPct: WIND_BESS_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: 0,
      };
    case 'grid-tariff':
      // Grid IMPORT (not generation) priced from GRID_TARIFF_BENCHMARKS so a
      // grid component inside a blend matches the standalone grid pathway's
      // basis (gridTariff.ts presetFor) — NO divergence between the two LCOEs:
      //   - capex = grid CONNECTION only (substation + transmission upgrade
      //     $/kW); NO generation-plant capex, because the grid supplies energy.
      //   - fixedOm = demand charge annualised ($/kW-month × 12 → $/kW-yr); the
      //     standalone pathway bills this on load-kW × 12 (gridTariff.ts:251-252),
      //     identical per-kW basis to the blend's kw × fixedOmPerKwYearUsd.
      //   - variableOm = all-in delivered ENERGY charge (energy charge +
      //     transmission tariff = 48 + 42 = $90/MWh) = standalone's
      //     `energyPerMwh`. `SourceEconomics` has no separate tariff field, so
      //     the blend folds BOTH terms here; the standalone splits them across
      //     `opex.variableOmPerMwhUsd` (energy component, 48) and
      //     `opex.transmissionTariffPerMwhUsd` (42) and the cashflow engine SUMS
      //     them (`annualVariableOmUsd`) — same $90/MWh, no divergence. Before
      //     R-131-FIN-DECISION-C the standalone put the ALL-IN 90 in
      //     `variableOmPerMwhUsd` while also setting the tariff field, so it
      //     really charged $132/MWh and this "no divergence" claim was false.
      //   - fuel = 0 — the grid has no fuel cost; the energy cost IS the tariff
      //     (already in variableOm), mirroring the standalone opex split.
      //   - tCo2ePerMwh = the grid import emissions factor, drawn from the SAME
      //     benchmark constant the standalone pathway uses for its carbon block.
      //
      // FLAGGED ASSUMPTION: the standalone pathway is SITE-AWARE for connection
      // capex (estimateSubstationCost reads voltage / tie-line distance / ISO
      // off the site to refine substation + tie-line $). blendPhaseEconomics has
      // no site context, so the grid component here uses the STATIC flat-fallback
      // benchmark $/kW (substationUsdPerKw + transmissionUpgradeUsdPerKw) — the
      // same number the standalone pathway falls back to when no grid-study data
      // is present. Site-aware refinement remains the standalone pathway's job;
      // it is intentionally NOT threaded through the blend in this fix.
      // Q8 — the delivery rates come from the SOURCED ISO table, exactly as the
      // standalone grid pathway now does, so the blended grid component and the
      // standalone pathway cannot drift apart. `blendPhaseEconomics` has no site
      // context, so this takes the table's default row (PJM industrial) rather
      // than a site-resolved one — a cited default, not an anonymous constant.
      const gridTariff = lookupTransmissionTariff('PJM', 'industrial');
      return {
        capexPerKwUsd:
          GRID_TARIFF_BENCHMARKS.substationUsdPerKw +
          GRID_TARIFF_BENCHMARKS.transmissionUpgradeUsdPerKw,
        fixedOmPerKwYearUsd: gridTariff.demandChargePerKwMonthUsd * 12,
        variableOmPerMwhUsd:
          gridTariff.energyChargePerMwhUsd +
          gridTariff.transmissionTariffPerMwhUsd,
        fuelCostPerMwhUsd: 0,
        capacityFactorPct: GRID_TARIFF_BENCHMARKS.capacityFactorPct,
        tCo2ePerMwh: GRID_TARIFF_BENCHMARKS.gridEmissionsTCo2ePerMwh,
      };
    case 'mixed':
    default:
      throw new RangeError(
        `sourceEconomicsFor: '${powerSource}' is not a point-source MIX component ` +
          `(supported: gas-cc, gas-sc, nuclear-smr, nuclear-large, solar-bess, wind-bess, grid-tariff)`,
      );
  }
}

export interface BlendedPhaseEconomics {
  /** Total blended capacity of the mix, MW (Σ fractionMw). */
  capacityMw: number;
  /** Capacity-weighted overnight capex for the generation mix, USD. */
  generationCapexUsd: number;
  /**
   * Shared interconnect/substation cost serving the blended load, USD.
   * Apportioned across sources pro-rata by capacity (MODELING ASSUMPTION —
   * callers must surface this in `flagsForUser`). Zero when not supplied.
   */
  sharedInterconnectUsd: number;
  /** generationCapexUsd + sharedInterconnectUsd, USD. */
  totalCapexUsd: number;
  /** Blended annual energy across all sources, MWh/yr (Σ MW × 8760 × CF). */
  annualMwh: number;
  /** Capacity-weighted fixed O&M for the mix, USD/yr. */
  annualFixedOmUsd: number;
  /** Energy-weighted variable O&M for the mix, USD/yr. */
  annualVariableOmUsd: number;
  /** Energy-weighted fuel cost for the mix, USD/yr. */
  annualFuelUsd: number;
  /** annualFixedOmUsd + annualVariableOmUsd + annualFuelUsd, USD/yr. */
  annualOpexUsd: number;
  /** Total annual operating emissions across the mix, tCO2e/yr. */
  annualTCo2e: number;
  /** Energy-weighted blended emissions intensity, tCO2e/MWh. */
  blendedTCo2ePerMwh: number;
}

/**
 * Compute a phase's capacity-WEIGHTED blended economics from its source mix.
 *
 * Dimensional analysis (per component i with capacity MWᵢ and factor CFᵢ):
 *   capexᵢ      = MWᵢ × 1000 kW/MW × ($/kW)            → USD
 *   energyᵢ     = MWᵢ × 8760 h/yr × (CFᵢ/100)          → MWh/yr
 *   fixedOmᵢ    = MWᵢ × 1000 kW/MW × ($/kW-yr)         → USD/yr
 *   variableOmᵢ = energyᵢ × ($/MWh)                    → USD/yr
 *   fuelᵢ       = energyᵢ × ($/MWh)                    → USD/yr
 *   emissionsᵢ  = energyᵢ × (tCO2e/MWh)                → tCO2e/yr
 *
 * Shared interconnect/substation serving the blended load is apportioned
 * pro-rata by capacity (MODELING ASSUMPTION). Since the apportionment is
 * additive across sources it nets to `sharedInterconnectUsdTotal`; the
 * pro-rata split is exposed via `apportionInterconnectByCapacity` for
 * per-source reporting.
 *
 * @param sharedInterconnectUsdTotal Shared substation/tie-line cost (USD)
 *   serving the whole blended phase. Default 0 (pure generation blend).
 */
export function blendPhaseEconomics(
  sourceMix: readonly PhaseSourceMixComponent[],
  sharedInterconnectUsdTotal = 0,
): BlendedPhaseEconomics {
  if (sourceMix.length === 0) {
    throw new RangeError('blendPhaseEconomics: sourceMix must be non-empty');
  }

  let capacityMw = 0;
  let generationCapexUsd = 0;
  let annualMwh = 0;
  let annualFixedOmUsd = 0;
  let annualVariableOmUsd = 0;
  let annualFuelUsd = 0;
  let annualTCo2e = 0;

  for (const component of sourceMix) {
    if (!(component.fractionMw > 0)) {
      throw new RangeError(
        `blendPhaseEconomics: fractionMw for '${component.powerSource}' must be > 0`,
      );
    }
    const econ = sourceEconomicsFor(component.powerSource, component.fuelCellSnapshot);
    const mw = component.fractionMw;
    const kw = mw * KW_PER_MW;
    const energyMwh = mw * HOURS_PER_YEAR * (econ.capacityFactorPct / 100);

    capacityMw += mw;
    generationCapexUsd += kw * econ.capexPerKwUsd;
    annualMwh += energyMwh;
    annualFixedOmUsd += kw * econ.fixedOmPerKwYearUsd;
    annualVariableOmUsd += energyMwh * econ.variableOmPerMwhUsd;
    annualFuelUsd += energyMwh * econ.fuelCostPerMwhUsd;
    annualTCo2e += energyMwh * econ.tCo2ePerMwh;
  }

  const sharedInterconnectUsd = Math.max(0, sharedInterconnectUsdTotal);
  const annualOpexUsd = annualFixedOmUsd + annualVariableOmUsd + annualFuelUsd;
  const blendedTCo2ePerMwh = annualMwh > 0 ? annualTCo2e / annualMwh : 0;

  return {
    capacityMw,
    generationCapexUsd,
    sharedInterconnectUsd,
    totalCapexUsd: generationCapexUsd + sharedInterconnectUsd,
    annualMwh,
    annualFixedOmUsd,
    annualVariableOmUsd,
    annualFuelUsd,
    annualOpexUsd,
    annualTCo2e,
    blendedTCo2ePerMwh,
  };
}

/**
 * Apportion a shared interconnect/substation cost across the mix sources
 * PRO-RATA BY CAPACITY (MODELING ASSUMPTION — flag in `flagsForUser`).
 *
 * Σ(apportioned) === sharedInterconnectUsdTotal (within fp tolerance), so
 * the apportionment is conservative — it neither creates nor destroys cost.
 */
export function apportionInterconnectByCapacity(
  sourceMix: readonly PhaseSourceMixComponent[],
  sharedInterconnectUsdTotal: number,
): Array<{ powerSource: TechnologyId; fractionMw: number; interconnectUsd: number }> {
  const totalMw = sourceMix.reduce((sum, c) => sum + c.fractionMw, 0);
  if (!(totalMw > 0)) {
    throw new RangeError('apportionInterconnectByCapacity: total fractionMw must be > 0');
  }
  return sourceMix.map((c) => ({
    powerSource: c.powerSource,
    fractionMw: c.fractionMw,
    interconnectUsd: sharedInterconnectUsdTotal * (c.fractionMw / totalMw),
  }));
}

/** Surface-layer view: blended LCOE-proxy + emissions for one mixed phase. */
export interface PhaseMixSummary {
  capacityMw: number;
  totalCapexUsd: number;
  annualMwh: number;
  annualOpexUsd: number;
  /**
   * Simple (un-discounted) LCOE proxy, USD/MWh — for quick Surface-layer
   * display only. The investment-grade, WACC-discounted LCOE remains
   * `computePathwayLcoe`; this is a lightweight blend indicator.
   *
   *   LCOE_proxy = (capexAnnualised + annualOpex) / annualMwh
   *   capexAnnualised = totalCapex / amortYears  (straight-line, no discount)
   */
  simpleLcoePerMwhUsd: number;
  blendedTCo2ePerMwh: number;
}

/**
 * Helper for the Surface layer: blended LCOE-proxy + emissions intensity
 * for a phase's source mix. Pull `amortYears` from the pathway design life.
 *
 * NOTE: `simpleLcoePerMwhUsd` is an UN-DISCOUNTED indicator (straight-line
 * capex amortisation), not the risk-adjusted LCOE — use
 * `computePathwayLcoe` for the investment-grade figure.
 */
export function summarizePhaseMix(
  sourceMix: readonly PhaseSourceMixComponent[],
  opts?: { sharedInterconnectUsdTotal?: number; amortYears?: number },
): PhaseMixSummary {
  const blend = blendPhaseEconomics(sourceMix, opts?.sharedInterconnectUsdTotal ?? 0);
  // Default straight-line amortisation horizon for the UN-DISCOUNTED display proxy
  // only (NOT the authoritative LCOE — see computePathwayLcoe). 25y reflects a
  // typical merchant-generation book/design life (NREL ATB project-life convention);
  // callers should pass the pathway's actual design life via opts.amortYears.
  const DEFAULT_PROXY_AMORT_YEARS = 25;
  const amortYears = opts?.amortYears ?? DEFAULT_PROXY_AMORT_YEARS;
  const capexAnnualisedUsd = amortYears > 0 ? blend.totalCapexUsd / amortYears : 0;
  const simpleLcoePerMwhUsd =
    blend.annualMwh > 0 ? (capexAnnualisedUsd + blend.annualOpexUsd) / blend.annualMwh : 0;
  return {
    capacityMw: blend.capacityMw,
    totalCapexUsd: blend.totalCapexUsd,
    annualMwh: blend.annualMwh,
    annualOpexUsd: blend.annualOpexUsd,
    simpleLcoePerMwhUsd,
    blendedTCo2ePerMwh: blend.blendedTCo2ePerMwh,
  };
}
